<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import Column from 'primevue/column';
import { FolderKanban, Plus, Search, Trash2 } from '@lucide/vue';
import { StatoProgetto, type PaginationQuery, type ProgettoListItem } from '@evolfin/shared';
import { useListaQuery } from '@/composables/useListaQuery';
import LinkScheda from '@/components/ui/LinkScheda.vue';
import TabellaDati from '@/components/ui/TabellaDati.vue';
import PulsanteAzione from '@/components/ui/PulsanteAzione.vue';
import PulsanteEsporta from '@/features/export/components/PulsanteEsporta.vue';
import { formattaData } from '@/utils/formato';
import { useAvvisi } from '@/composables/useAvvisi';
import { useConferma } from '@/composables/useConferma';
import { useAuthStore } from '@/stores/auth';
import { EvolfinApiError } from '@/api/client';
import { eliminaProgetto, listaProgetti, type QueryProgetti } from '../api';
import { coloriStatoProgetto } from '../stati';

definePage({ meta: { titolo: 'Progetti' } });

const router = useRouter();
const auth = useAuthStore();
const avvisi = useAvvisi();
const conferma = useConferma();

const puoEliminare = computed(() => auth.ha('can_delete_progetti'));

const lista = useListaQuery<ProgettoListItem>(
  'progetti',
  (q) => listaProgetti(q as QueryProgetti),
  { iniziale: { sort: 'creatoIl', order: 'desc' } as unknown as Partial<PaginationQuery> },
);
const { righe, totale, query, caricamento, onPage, onSort, cerca } = lista;

const testoRicerca = ref('');

const statoFiltro = computed<string>({
  get: () => (query as QueryProgetti).stato ?? '',
  set: (v) => {
    (query as QueryProgetti).stato = (v || undefined) as QueryProgetti['stato'];
    query.page = 1;
  },
});

function apriCreazione(): void {
  void router.push('/progetti/nuovo');
}

function vaiAlDettaglio(p: ProgettoListItem): void {
  void router.push(`/progetti/${p.id}`);
}

async function elimina(p: ProgettoListItem): Promise<void> {
  if (
    !(await conferma({
      titolo: 'Elimina progetto',
      messaggio: `Eliminare ${p.nome}? Sparirà da elenchi, ricerche e task. I documenti già caricati restano archiviati, ma il progetto non sarà più consultabile.`,
      etichettaConferma: 'Elimina',
      distruttiva: true,
    }))
  ) {
    return;
  }
  try {
    await eliminaProgetto(p.id);
    avvisi.successo('Progetto eliminato');
    await lista.ricarica();
  } catch (errore) {
    const messaggio =
      errore instanceof EvolfinApiError ? errore.message : 'Operazione non riuscita';
    avvisi.errore('Impossibile eliminare', messaggio);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <header class="flex flex-col items-stretch gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-xs font-semibold tracking-[0.08em] text-ink-muted uppercase">Pratiche</p>
        <h1 class="mt-1 font-display text-[33px] leading-[1.07] font-semibold">Progetti</h1>
        <p class="mt-1 hidden text-[13px] text-ink-secondary sm:block">
          Le pratiche di finanza agevolata seguite per i clienti.
        </p>
      </div>
      <PulsanteAzione class="w-full sm:w-auto" @click="apriCreazione">
        Nuovo progetto
        <template #icona><Plus :size="16" :stroke-width="2" /></template>
      </PulsanteAzione>
    </header>

    <div class="card p-0">
      <div class="flex flex-wrap items-center gap-3 border-b border-line-soft px-4 py-3">
        <label class="relative min-w-[14rem] flex-1">
          <Search
            class="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-ink-muted"
            :stroke-width="2"
            aria-hidden="true"
          />
          <input
            v-model="testoRicerca"
            type="search"
            placeholder="Cerca per nome…"
            class="w-full rounded-sm border border-line bg-surface py-2 pr-3 pl-9 text-[13px] text-ink transition-[border-color] focus:border-[var(--color-accent)]"
            @input="cerca(testoRicerca)"
          />
        </label>

        <select v-model="statoFiltro" class="filtro" aria-label="Filtra per stato">
          <option value="">Tutti gli stati</option>
          <option v-for="s in StatoProgetto.options" :key="s" :value="s">{{ s }}</option>
        </select>

        <PulsanteEsporta
          dataset="progetti"
          endpoint="/api/progetti/export"
          :query="query"
          :totale="totale"
          nome-file="progetti.csv"
        />
      </div>

      <TabellaDati
        :righe="righe"
        :totale="totale"
        :query="query"
        :caricamento="caricamento"
        @page="onPage"
        @sort="onSort"
      >
        <Column field="nome" header="Progetto" :sortable="true" primaria>
          <template #body="{ data }: { data: ProgettoListItem }">
            <router-link
              :to="`/progetti/${data.id}`"
              class="flex items-center gap-2.5 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            >
              <span
                class="grid size-8 shrink-0 place-items-center rounded-md bg-[var(--surface-accent-soft)] text-[var(--color-primary)]"
                aria-hidden="true"
              >
                <FolderKanban :size="16" :stroke-width="1.9" />
              </span>
              <div class="min-w-0">
                <p class="truncate font-medium text-ink hover:text-[var(--color-primary)]">
                  {{ data.nome }}
                </p>
                <p class="truncate text-[12px] text-ink-muted tabular-nums">
                  {{ data.codiceInterno }}
                </p>
              </div>
            </router-link>
          </template>
        </Column>

        <Column field="clienteRagioneSociale" header="Cliente" :sortable="false">
          <template #body="{ data }: { data: ProgettoListItem }">
            <LinkScheda :a="`/clienti/${data.clienteId}`" class="text-[13px] text-ink-secondary">
              {{ data.clienteRagioneSociale }}
            </LinkScheda>
          </template>
        </Column>

        <Column field="tipologiaPratica" header="Tipologia" :sortable="false">
          <template #body="{ data }: { data: ProgettoListItem }">
            <span v-if="data.tipologiaPratica" class="text-[13px] text-ink-secondary">
              {{ data.tipologiaPratica }}
            </span>
            <span v-else class="text-[12px] text-ink-muted">—</span>
          </template>
        </Column>

        <Column field="stato" header="Stato" :sortable="true">
          <template #body="{ data }: { data: ProgettoListItem }">
            <span
              class="rounded-pill px-2 py-0.5 text-[11px] font-medium"
              :style="{
                background: `color-mix(in oklch, ${coloriStatoProgetto[data.stato] ?? 'var(--color-info)'} 12%, transparent)`,
                color: coloriStatoProgetto[data.stato] ?? 'var(--color-info)',
              }"
            >
              {{ data.stato }}
            </span>
          </template>
        </Column>

        <Column field="creatoIl" header="Creato il" :sortable="true">
          <template #body="{ data }: { data: ProgettoListItem }">
            <span class="text-[12.5px] text-ink-secondary tabular-nums">
              {{ formattaData(data.creatoIl) }}
            </span>
          </template>
        </Column>

        <Column header="" azioni :style="{ width: '5rem' }">
          <template #body="{ data }: { data: ProgettoListItem }">
            <div class="flex items-center justify-end gap-1" @click.stop>
              <button
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-ink"
                title="Apri progetto"
                @click="vaiAlDettaglio(data)"
              >
                <FolderKanban :size="16" :stroke-width="1.8" />
              </button>
              <button
                v-if="puoEliminare"
                type="button"
                class="rounded-sm p-1.5 text-ink-muted transition-colors hover:bg-soft hover:text-[var(--color-danger-text)]"
                title="Elimina"
                @click="elimina(data)"
              >
                <Trash2 :size="16" :stroke-width="1.8" />
              </button>
            </div>
          </template>
        </Column>

        <template #vuoto>
          <p class="py-10 text-center text-[13px] text-ink-secondary">Nessun progetto trovato.</p>
        </template>
      </TabellaDati>
    </div>
  </div>
</template>

<style scoped>
.filtro {
  border-radius: var(--radius-sm);
  border: 1px solid var(--color-border);
  background-color: var(--color-surface);
  padding-block: 0.5rem;
  padding-inline-start: 0.65rem;
  font-size: 13px;
  color: var(--color-text);
  transition: border-color 120ms ease;
}
.filtro:focus {
  outline: none;
  border-color: var(--color-accent);
}
</style>
